/**
 * @module AuthenticationFormContext
 * @description Context for managing authentication form state and
 *  transitions between different forms
 */

import React, {
  createContext, useContext, useReducer,
} from 'react';
import { AUTH_FORMS } from './constants';
import { AuthenticationFormContextProviderPropTypes, AuthenticationFormContextProviderDefaultProps } from './props';

/**
 * @constant {React.Context} AuthenticationFormContext
 * @description Context for sharing authentication form state across components
 * @default { form: AUTH_FORMS.SIGN_IN, redirect: undefined, prePopulatedEmail: '' }
 */
const AuthenticationFormContext = createContext({ form: AUTH_FORMS.SIGN_IN, redirect: undefined, prePopulatedEmail: '' });

/**
 * @constant {React.Context} AuthenticationFormDispatchContext
 * @description Context for sharing authentication form dispatch function across components
 * @default null
 */
const AuthenticationFormDispatchContext = createContext(null);

/**
 * @function authenticationFormReducer
 * @description Reducer function for managing authentication form state
 * @param {Object} state - Current state of the authentication form
 * @param {Object} action - Action to perform on the state
 * @param {string} action.type - Type of action to perform
 * @param {string} [action.form] - Form to display when action type is 'showForm'
 * @param {string} [action.redirect] - Redirect URL after form submission
 * @param {Object} [action.user] - User information when action type is 'existingUser'
 * @returns {Object} New state after applying the action
 */
const authenticationFormReducer = (state, action) => {
  switch (action.type) {
    case 'showForm':
      return {
        ...state,
        form: action.form,
        redirect: action?.redirect,
        prePopulatedEmail: action?.prePopulatedEmail,
      };
    case 'existingUser':
      return { ...state, user: action.user };
    default:
      return state;
  }
};

/**
 * @function AuthenticationFormContextProvider
 * @description Provider component that makes authentication form state and
 *  dispatch available to child components
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components that will have access to the context
 * @param {string} props.form - Initial form to display
 * @param {string} [props.redirect] - Initial redirect URL
 * @returns {React.Element} Provider component with authentication form context
 *
 * @example
 * <AuthenticationFormContextProvider form={AUTH_FORMS.SIGN_IN} redirect="/account">
 *   <LoginPage />
 * </AuthenticationFormContextProvider>
 */
export function AuthenticationFormContextProvider({
  children,
  form,
  redirect,
  prePopulatedEmail,
}) {
  const [state, dispatch] = useReducer(authenticationFormReducer, { form, redirect, prePopulatedEmail });

  return (
    <AuthenticationFormContext.Provider value={state}>
      <AuthenticationFormDispatchContext.Provider value={dispatch}>
        { children }
      </AuthenticationFormDispatchContext.Provider>
    </AuthenticationFormContext.Provider>
  );
}

AuthenticationFormContextProvider.propTypes = AuthenticationFormContextProviderPropTypes;
AuthenticationFormContextProvider.defaultProps = AuthenticationFormContextProviderDefaultProps;

/**
 * @function useAuthenticationForm
 * @description Custom hook to access the authentication form state
 * @returns {Object} Current authentication form state
 *
 * @example
 * const { form, redirect } = useAuthenticationForm();
 * if (form === AUTH_FORMS.SIGN_IN) {
 *   // Render sign in form
 * }
 */
export const useAuthenticationForm = () => useContext(AuthenticationFormContext);

/**
 * @function useAuthenticationFormDispatch
 * @description Custom hook to access the authentication form dispatch function
 * @returns {Function} Dispatch function to update authentication form state
 *
 * @example
 * const dispatch = useAuthenticationFormDispatch();
 * dispatch({ type: 'showForm', form: AUTH_FORMS.SIGN_UP });
 */
export const useAuthenticationFormDispatch = () => useContext(AuthenticationFormDispatchContext);
